'use client';

import { buildManagerUserUrl } from '@/domains/routing';
import { ROUTES } from '@/constants';
import { useGlobalStore } from '@/store/global-store';
import { useRouter } from 'next/navigation';
import type { IUser } from '@/types';

/**
 * Centralized app navigation hook.
 *
 * Why this hook exists:
 * - Keeps route transitions in one place instead of scattering `router.push(...)`
 *   calls across components.
 * - Ensures route constants and URL builders are used consistently.
 * - Co-locates navigation-side effects (for example, setting active managed user
 *   state before moving to a manager detail page).
 *
 * Design notes:
 * - Uses Next.js App Router's `useRouter()` for imperative navigation.
 * - Uses global store actions for cross-page context that should persist during
 *   route transitions.
 * - Returns small, focused helper methods so consuming components can keep JSX
 *   event handlers simple and readable.
 */
export function useAppRouting() {
  // Next.js router instance used for push/back navigation actions.
  const router = useRouter();

  // Pull only the store action(s) this hook needs.
  // This keeps the hook focused and avoids coupling to unrelated store state.
  const { setActiveUser } = useGlobalStore();

  /**
   * Navigate to the sales rep dashboard root.
   *
   * Uses a route constant so path changes are managed centrally.
   */
  const navigateToDashboard = () => {
    return router.push(ROUTES.SALES_REP_DASHBOARD);
  };

  /**
   * Navigate to the manager dashboard root.
   */
  const navigateToManagerDashboard = () => {
    return router.push(ROUTES.MANAGER_DASHBOARD);
  };

  /**
   * Navigate to manager detail view for a specific user.
   *
   * Side effect:
   * - Saves the selected user into global state before navigation so downstream
   *   manager views can immediately access context.
   *
   * @see FEATURE_FLAGS.ENABLE_MANAGER_API_ENDPOINTS = false
   */
  const navigateToManagedUser = (user: IUser) => {
    // Keep selected user context in store for manager workflows that depend on it.
    setActiveUser(user);

    // Route to nested manager user detail URL.
    return router.push(buildManagerUserUrl(user.uuid));
  };

  /**
   * Navigate to manager summary/detail URL by user ID.
   *
   * This variant intentionally accepts a user ID instead of a full `IUser`
   * object, and supports optional query parameters for deep-link scenarios.
   *
   * @see FEATURE_FLAGS.ENABLE_MANAGER_API_ENDPOINTS = true
   */
  const navigateToManagedUserSummary = (userId: string, queryParams?: Record<string, string>) => {
    return router.push(buildManagerUserUrl(userId, queryParams));
  };

  /**
   * Navigate to the previous history entry.
   *
   * Useful for generic "Back" actions where destination should follow browser
   * history semantics instead of a fixed route.
   */
  const navigateBack = () => {
    return router.back();
  };

  /**
   * Navigate to the login route.
   */
  const navigateToLogin = () => {
    return router.push(ROUTES.LOGIN);
  };

  // ====================================================
  // Return the navigation methods for use in components
  // ====================================================
  return {
    navigateBack,
    navigateToDashboard,
    navigateToLogin,
    navigateToManagedUser,
    navigateToManagedUserSummary,
    navigateToManagerDashboard,
  };
}
